import { useCallback, useEffect, useMemo } from 'react';

import {
  useDismissGettingStartedMutation,
  useGuardianPlayersQuery,
  useGuardianProfileQuery,
  useStartGettingStartedMutation,
  useUpdateGettingStartedMutation,
} from '@/features/guardian/queries';
import { getWelcomeCompletedStep } from '@/features/guardian/services/save-welcome-progress';
import { useSessionStore } from '@/stores';

export type KidCardData = {
  name: string;
  level: number;
  progress: number;
  avatarUrl?: string;
};

export function useGuardianHomePlayers() {
  const uid = useSessionStore(s => s.user?.uid);
  const {
    data: guardianPlayers,
    refetch,
    isLoading: isLoadingPlayers,
  } = useGuardianPlayersQuery(uid);

  const hasRealPlayers = (guardianPlayers && guardianPlayers.length > 0) ?? false;
  const hasPlayers = hasRealPlayers;

  const playerName = useMemo(() => {
    const first = guardianPlayers?.[0];
    return first?.firstname?.trim();
  }, [guardianPlayers]);

  // Avatar of the first player, shown next to the name in the Get Started "Add a Player" step.
  const playerAvatarUrl = guardianPlayers?.[0]?.avatarUrl;

  // The single-player card shown in the Welcome flow's My Kids (built from the real player).
  const kidCard = useMemo((): KidCardData => {
    const first = guardianPlayers?.[0];
    return {
      name: [first?.firstname, first?.lastname].filter(Boolean).join(' ').trim(),
      level: first?.level ?? 1,
      progress: 0,
      avatarUrl: first?.avatarUrl,
    };
  }, [guardianPlayers]);

  // Welcome / Get Started progress — server is the source of truth (the profile always carries a
  // `gettingStarted` block). Show the flow while it's in progress; hide once the server marks it
  // `completed`. While the profile is still loading (no block yet) keep it hidden to avoid a flash.
  const { data: guardian } = useGuardianProfileQuery(uid);
  const { mutate: startWelcome } = useStartGettingStartedMutation(uid);
  const { mutate: markStep } = useUpdateGettingStartedMutation(uid);
  const { mutate: dismissGuide } = useDismissGettingStartedMutation(uid);

  const gettingStarted = guardian?.gettingStarted;
  const completedStep = getWelcomeCompletedStep(gettingStarted);
  // Reactive to server state only — no Home-side effect (an effect runs AFTER the first render, so
  // it would flash the welcome then hide it). The orphaned-guide case (has players, no guide) is
  // dismissed BEFORE Home in the routing flow, not here.
  const showOnboarding = gettingStarted ? gettingStarted.status === 'in_progress' : !hasRealPlayers;

  const markWelcomeStep = useCallback(
    (step: number) => {
      // Skip steps already marked done — re-marking returns 409 Conflict from the server.
      if (gettingStarted?.steps?.[String(step)] === 'done') return;
      markStep(step);
    },
    [gettingStarted, markStep],
  );
  // Tapping "Book a lesson" (step 2) or "Track progress" (step 3) closes the guide early.
  const dismissWelcome = useCallback(() => {
    if (showOnboarding) dismissGuide();
  }, [showOnboarding, dismissGuide]);
  //init welcom
  useEffect(() => {
    Promise.resolve().then(() => {
      if (guardian && !gettingStarted && hasRealPlayers) {
        dismissGuide();
      } else if (!gettingStarted && !hasRealPlayers) {
        startWelcome();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    guardianPlayers,
    refetchGuardianPlayers: refetch,
    hasRealPlayers,
    hasPlayers,
    playerName,
    playerAvatarUrl,
    kidCard,
    showOnboarding,
    welcomeCompletedStep: completedStep,
    markWelcomeStep,
    dismissWelcome,
    isLoading: isLoadingPlayers,
  };
}
